import type { InstancesEntityData } from '@/login/types/AccessTokenData';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { InstanceRole, NewCurrentRole } from '@sysarb/sysarb-types/services/v1/authentication';
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, type ComputedRef } from 'vue';
import { useResgate } from '@/login/composables/useResgate';
import { InstanceAccountType, Role } from '@sysarb/sysarb-types/service-base/enums';
import { appSettings } from '@/appSettings';
import { useMagicKeys } from '@vueuse/core';

type CurrentUserReturn = {
    roles: ComputedRef<InstanceRole[]>;
    /**
     * The current active role of the user in the selected instance.
     */
    role: ComputedRef<InstanceRole | null>;
    /**
     * Set the role of the user in the selected instance.
     */
    setRole: (roleJunctionID: string) => Promise<void>;
    /**
     * Set the language of the user.
     */
    setLanguage: (langugage: string) => Promise<void>;
    /**
     * If the user is a support user.
     */
    isSupport: ComputedRef<boolean>;
    /**
     * If the user is a consultant user.
     */
    isConsultant: ComputedRef<boolean>;
    /**
     * The instances the user has access to.
     */
    instances: ComputedRef<InstancesEntityData[]>;

    isEmployee: ComputedRef<boolean>;
    isManager: ComputedRef<boolean>;
    isUnion: ComputedRef<boolean>;
    isAdministrator: ComputedRef<boolean>;
    isSalarySurveyAnalyst: ComputedRef<boolean>;
    isOperationalAdministrator: ComputedRef<boolean>;
    isSectionManager: ComputedRef<boolean>;
    isReadOnly: ComputedRef<boolean>;
    isSystemUser: ComputedRef<boolean>;
    bypassOrganisation: ComputedRef<boolean>;
    currentOrganisationNodeId: ComputedRef<string | null>;
    instanceAccountType: ComputedRef<InstanceAccountType | null>;

    /**
     * The name of the user.
     */
    name: ComputedRef<string>;

    /**
     * The first name of the user.
     */
    firstName: ComputedRef<string | null>;

    /**
     * The email of the user.
     */
    email: ComputedRef<string | null>;
    /**
     * Username from login service.
     */
    username: ComputedRef<string | null>;
    /**
     * Users selected language.
     */
    language: ComputedRef<string | null>;

    loginUserId: ComputedRef<string | null>;
    instanceUserId: ComputedRef<string | null>;
    managerId: ComputedRef<string | null>;
    employeeId: ComputedRef<string | null>;

    /**
     * Check if an instance is active.
     */
    isInstanceActive: (subdomain: string) => boolean;

    // From old useSupport().
    showSupportData: ComputedRef<boolean>;
    isDev: boolean;
    isLocal: boolean;
    isTranslator: boolean;
    hasInstances: ComputedRef<boolean>;
    isActiveInstance: ComputedRef<boolean>;
};

export function useUser(): CurrentUserReturn {
    const loginStore = useLoginStore();
    const { userData, selectedInstance, userId, instanceTopic, employee, instance } = storeToRefs(loginStore);
    const { shift, alt } = useMagicKeys();
    const isDev = import.meta.env.DEV === true;
    const isLocal = window.location.hostname === 'localhost';
    const resgate = useResgate(crypto.randomUUID());

    async function setRole(roleJunctionID: string): Promise<void> {
        if (instanceTopic.value == null || userId.value == null) {
            console.error('setRole', 'Missing required parameters');
            return;
        }

        const data: NewCurrentRole = {
            roleJunctionID,
            instanceAccountType: InstanceAccountType.ServerUser
        };

        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.user`, 'setrole', data);
    }

    async function setLanguage(language: string): Promise<void> {
        if (userId.value == null) {
            console.error('setLanguage', 'Missing required parameters');
            return;
        }

        await resgate.call<void>(`v2.access.token.${userId.value}.user`, 'setlanguage', { language });
    }

    const loginUser = computed(() => userData.value?.loginUser ?? null);

    const instanceUser = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.user ?? null;
    });

    const instances = computed(() => {
        if (userData.value == null) return [];
        if (userData.value.calculatedInstances == null) return [];

        return Array.from(userData.value.calculatedInstances);
    });

    const hasInstances = computed(() => {
        if (userData.value == null) return false;
        if (userData.value.instances == null) return false;

        return true;
    });

    const roles = computed(() => {
        if (instanceUser.value == null) return [];
        return instanceUser.value.roles ?? [];
    });

    const role = computed(() => {
        if (roles.value.length === 0) return null;
        return roles.value.find(c => c.isActive) ?? null;
    });

    const isSupport = computed(() => {
        if (loginUser.value == null) return false;
        return loginUser.value.accountType === InstanceAccountType.Support;
    });

    const isConsultant = computed(() => {
        if (loginUser.value == null) return false;
        return loginUser.value.accountType === InstanceAccountType.Consultant;
    });

    const isInstanceActive = (subdomain: string): boolean => {
        const foundInstance = instances.value.find(c => c.SubDomain === subdomain);

        if (isSupport.value === true) return true;
        if (foundInstance == null) return false;
        if (foundInstance.user == null) return false;
        if (foundInstance.user.Inactive === true) return false;

        return true;
    };

    const isActiveInstance = computed(() => {
        if (instances.value.length === 0) return false;

        const item = instances.value.find(c => c.SubDomain === instance.value);
        if (item == null) return false;
        if (item.user == null) return false;
        return item.user.Inactive === false;
    });

    const isManager = computed<boolean>(() => role.value?.role === Role.Manager);

    const isUnion = computed<boolean>(() => role.value?.role === Role.Union);

    const isAdministrator = computed<boolean>(() => role.value?.role === Role.Administrator || role.value?.role === Role.Root || role.value?.role === Role.SystemAdministrator || role.value?.role === Role.Consultant);

    const isSalarySurveyAnalyst = computed<boolean>(() => role.value?.role === Role.SalarySurveyAnalyst);

    const isOperationalAdministrator = computed<boolean>(() => role.value?.role === Role.OperationalAdministrator);

    const isSectionManager = computed<boolean>(() => role.value?.role === Role.SectionManager);

    const isReadOnly = computed<boolean>(() => role.value?.role === Role.ReadOnly);

    const isEmployee = computed<boolean>(() => role.value?.role === Role.Employee);

    const isSystemUser = computed(() => {
        if (loginUser.value?.accountType == null) return false;

        if (loginUser.value.accountType === InstanceAccountType.Consultant) return true;
        if (loginUser.value.accountType === InstanceAccountType.Root) return true;
        if (loginUser.value.accountType === InstanceAccountType.Support) return true;

        if (role.value == null) return false;
        if (role.value.role === Role.Root) return true;
        if (role.value.role === Role.SystemAdministrator) return true;
        if (role.value.role === Role.Consultant) return true;

        return false;
    });

    const bypassOrganisation = computed(() => {
        if (role.value == null) return false;

        if (loginUser.value?.accountType != null && (role.value.role === Role.Root || role.value.role === Role.SystemAdministrator || role.value.role === Role.Consultant)) {
            switch (loginUser.value.accountType) {
                case InstanceAccountType.Consultant:
                case InstanceAccountType.Developer:
                case InstanceAccountType.Root:
                case InstanceAccountType.Support: {
                    return true;
                }
                default: break;
            }
        }

        switch (role.value.role) {
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Administrator:
            case Role.Consultant: {
                return true;
            }
            default: {
                return false;
            }
        }
    });

    const currentOrganisationNodeId = computed(() => {
        if (instanceUser.value == null) return null;
        if (instanceUser.value.roles == null) return null;
        return instanceUser.value.roles.find(c => c.isActive)?.organisationNodeId ?? null;
    });

    const instanceAccountType = computed(() => {
        if (loginUser.value == null) return null;
        return loginUser.value.accountType;
    });

    const name = computed(() => {
        if (isSupport.value === true) return 'Support';

        if (employee.value != null) {
            let _name = '';

            if (employee.value.forename !== '') _name += employee.value.forename;
            if (employee.value.surname !== '') _name += ` ${employee.value.surname}`;

            if (_name !== '') {
                return _name;
            }
        }

        if (loginUser.value == null) {
            return '';
        }

        return loginUser.value.FullName;
    });

    const firstName = computed(() => {
        // Return the forename if available.
        if (employee.value?.forename != null && employee.value.forename !== '') {
            return employee.value.forename;
        }

        // otherwise, try to split the full name.
        if (name.value !== '') {
            const parts = name.value.split(' ');
            if (parts.length > 0) {
                return parts[0];
            }
        }

        // Fallback to the value of name.
        return name.value;
    });

    const email = computed(() => {
        if (loginUser.value == null) return '';
        return loginUser.value.Email ?? null;
    });

    const loginUserId = computed(() => {
        if (loginUser.value == null) return null;
        return loginUser.value.Id;
    });

    const instanceUserId = computed(() => {
        if (instanceUser.value == null) return null;
        return instanceUser.value.ID ?? null;
    });

    const username = computed(() => {
        if (loginUser.value == null) return null;
        return loginUser.value.Username ?? null;
    });

    const language = computed(() => {
        if (loginUser.value == null) return null;
        return loginUser.value.Lang ?? null;
    });

    const managerId = computed(() => {
        if (instanceUser.value == null) return null;
        return instanceUser.value.Manager_ID ?? null;
    });

    const employeeId = computed(() => {
        if (instanceUser.value == null) return null;
        return instanceUser.value.Employee_ID ?? null;
    });

    const isTranslator = appSettings.enableTranslations;

    const showSupportData = computed(() => {
        const shiftPressed = shift.value;
        const altPressed = alt.value;
        const enabled = isDev || isSupport.value;

        if (shiftPressed && altPressed && enabled) return true;
        return false;
    });

    onUnmounted(() => {
        resgate.unsubscribe();
    });

    return {
        roles,
        role,
        setRole,
        setLanguage,
        isEmployee,
        isSupport,
        isConsultant,
        instances,
        isManager,
        isUnion,
        isAdministrator,
        isSalarySurveyAnalyst,
        isOperationalAdministrator,
        isSectionManager,
        isReadOnly,
        isSystemUser,
        isInstanceActive,
        bypassOrganisation,
        currentOrganisationNodeId,
        instanceAccountType,
        name,
        firstName,
        email,
        loginUserId,
        instanceUserId,
        username,
        language,
        managerId,
        employeeId,
        showSupportData,
        isDev,
        isLocal,
        isTranslator,
        hasInstances,
        isActiveInstance
    };
}
